import { create } from '@thoughtspot/logger';
import _ from 'lodash';
import { getEventSession } from './event-session-service';
import { PeopleIncrementalPropertiesEnum, PeoplePropertiesEnum, PeopleSetPropertiesEnum, updatePeopleProperty, } from './mixpanel-people-properties';
import { SessionPropsEnum, } from './mixpanel-session-properties';
let mixpanelDNT = false;
export let mixpanel;
let mixpanelSessionProperties;
let isMixpanelEnabled;
let isSessionReplayEnabled = false;
let isABTestingEnabled = false;
let mixpanelIsReady = false;
let pendingCallbacks = [];
export const getMixpanelReady = () => {
    if (mixpanelIsReady)
        return Promise.resolve();
    return new Promise(resolve => pendingCallbacks.push(resolve));
};
const Logger = create('mixpanel-service');
const notifyReady = () => {
    if (mixpanelIsReady)
        return;
    mixpanelIsReady = true;
    pendingCallbacks.splice(0).forEach(cb => cb());
};
const sessionReplayConfig = {
    record_heatmap_data: true,
    record_mask_all_text: true,
};
const getSessionReplayConfig = (enableSessionReplay) => enableSessionReplay ? sessionReplayConfig : {};
const getApiHostConfig = (apiHost) => apiHost ? { api_host: apiHost } : {};
const getFlagsConfig = (enableABTesting, teamId) => enableABTesting && !!teamId ? { flags: { context: { teamId } } } : {};
const REQUIRED_MIXPANEL_LIB_KEYS = [
    'init',
    'track',
    'identify',
    'set_config',
    'has_opted_out_tracking',
    'opt_in_tracking',
    'clear_opt_in_out_tracking',
];
const REQUIRED_MIXPANEL_PEOPLE_KEYS = [
    'set',
    'set_once',
    'increment',
    'union',
];
export var MixpanelCheckScope;
(function (MixpanelCheckScope) {
    MixpanelCheckScope["CORE"] = "CORE";
    MixpanelCheckScope["PEOPLE"] = "PEOPLE";
})(MixpanelCheckScope || (MixpanelCheckScope = {}));
export function hasRequiredMixpanelMethods(lib, target = MixpanelCheckScope.CORE) {
    switch (target) {
        case MixpanelCheckScope.PEOPLE:
            return REQUIRED_MIXPANEL_PEOPLE_KEYS.every(key => { var _a; return typeof ((_a = lib.people) === null || _a === void 0 ? void 0 : _a[key]) === 'function'; });
        case MixpanelCheckScope.CORE:
            return REQUIRED_MIXPANEL_LIB_KEYS.every(key => typeof lib[key] === 'function');
        default: {
            const exhaustiveCheck = target;
            Logger.warn(`Unknown MixpanelCheckScope: ${exhaustiveCheck}`);
            return false;
        }
    }
}
export async function initService(token, mixpanelLib, mixpanelSessionProps, debugMode, mixpanelApiHost, enableCrossSubDomainCookie, useLocalStorage, enableABTesting, enableSessionReplay, teamId) {
    mixpanel = mixpanelLib;
    const persistence = useLocalStorage ? 'localStorage' : 'cookie';
    mixpanel.init(token, {
        cross_subdomain_cookie: enableCrossSubDomainCookie,
        persistence,
        ...getApiHostConfig(mixpanelApiHost),
        ...getFlagsConfig(enableABTesting, teamId),
        ...getSessionReplayConfig(enableSessionReplay),
    });
    if (debugMode) {
        mixpanel.set_config({ debug: true });
    }
    mixpanel.set_config({ ignore_dnt: true });
    mixpanelSessionProperties = mixpanelSessionProps;
    isMixpanelEnabled = true;
    isSessionReplayEnabled = enableSessionReplay;
    isABTestingEnabled = enableABTesting;
    return true;
}
export function uploadEvent(eventId, eventProps) {
    mixpanel.track(eventId, eventProps);
}
export function uploadEventBatch(eventBatch) {
    eventBatch.forEach((evt) => {
        uploadEvent(evt.id, evt.props);
    });
}
export function isBlocked(url = 'https://api.mixpanel.com/') {
    return fetch(url, { mode: 'no-cors' })
        .then(() => false)
        .catch(() => true);
}
export function isOptedOut() {
    return mixpanel.has_opted_out_tracking();
}
export function setOptInTracking() {
    mixpanel.clear_opt_in_out_tracking();
    mixpanel.opt_in_tracking();
}
export function updateMixPanelProperty(key, value) {
    if (!isMixpanelEnabled) {
        return;
    }
    const isInPeopleProperties = _.includes(_.values(PeoplePropertiesEnum), key) ||
        _.includes(_.values(PeopleIncrementalPropertiesEnum), key) ||
        _.includes(_.values(PeopleSetPropertiesEnum), key);
    const isInSessionProperties = _.includes(_.values(SessionPropsEnum), key);
    if (!isInPeopleProperties && !isInSessionProperties) {
        throw new Error('Property does not exists in current set of properties');
    }
    if (isInPeopleProperties) {
        updatePeopleProperty(key, value);
    }
    if (isInSessionProperties) {
        mixpanelSessionProperties.updateSessionProperty(key, value);
    }
}
export function getSessionProperties() {
    return getEventSession();
}
export function isMixpanelDNT() {
    return mixpanelDNT;
}
export function setMixpanelDNT(value) {
    mixpanelDNT = value;
}
export const setMixpanelGroup = (groupKey, groupId) => {
    var _a;
    if (!isMixpanelEnabled) {
        return;
    }
    try {
        (_a = mixpanel.set_group) === null || _a === void 0 ? void 0 : _a.call(mixpanel, groupKey, groupId);
    }
    catch (e) {
        Logger.warn('setMixpanelGroup failed', e);
    }
};
export const startSessionReplay = () => {
    var _a;
    if (!isSessionReplayEnabled) {
        return;
    }
    try {
        (_a = mixpanel.start_session_recording) === null || _a === void 0 ? void 0 : _a.call(mixpanel);
    }
    catch (e) {
        Logger.warn('Session replay start failed', e);
    }
};
export const stopSessionReplay = () => {
    var _a;
    if (!isSessionReplayEnabled) {
        return;
    }
    try {
        (_a = mixpanel.stop_session_recording) === null || _a === void 0 ? void 0 : _a.call(mixpanel);
    }
    catch (e) {
        Logger.warn('Session replay stop failed', e);
    }
};
export const resetMixpanelOnLogout = () => {
    var _a;
    try {
        stopSessionReplay();
        (_a = mixpanel === null || mixpanel === void 0 ? void 0 : mixpanel.reset) === null || _a === void 0 ? void 0 : _a.call(mixpanel);
    }
    catch (error) {
        Logger.error('Error occurred while resetting Mixpanel:', error);
    }
    mixpanelIsReady = false;
    pendingCallbacks = [];
};
export const notifyMixpanelIdentified = () => {
    notifyReady();
};
export const notifyMixpanelDisabled = () => {
    notifyReady();
};
export const getVariantValue = async (flagName, fallbackValue) => {
    var _a, _b, _c;
    if (!isABTestingEnabled ||
        !isMixpanelEnabled ||
        !((_a = mixpanel === null || mixpanel === void 0 ? void 0 : mixpanel.flags) === null || _a === void 0 ? void 0 : _a.get_variant_value)) {
        return fallbackValue;
    }
    try {
        return await ((_c = (_b = mixpanel.flags).get_variant_value) === null || _c === void 0 ? void 0 : _c.call(_b, flagName, fallbackValue));
    }
    catch (e) {
        Logger.warn('Feature flag fetch failed', e);
        return fallbackValue;
    }
};
//# sourceMappingURL=mixpanel-service.js.map